import { Injectable } from '@angular/core';
import { Store } from '../models/store';

/**
 * Applies the merchant-selected storefront theme (ENG-5046) by setting CSS
 * custom properties on <html>, plus a `storefront-themed` class that gates
 * the themed style overrides. With the `storefront_theming_enabled` feature
 * flag off (or no store loaded) everything is removed, so the app renders
 * exactly as before theming existed.
 *
 * Theme variables are cached in localStorage so that on page reload the
 * theme renders instantly — before the store API call completes.
 */
@Injectable({ providedIn: 'root' })
export class StorefrontThemeService {

  static readonly THEME_CLASS = 'storefront-themed';
  static readonly SHARP_CLASS = 'storefront-corner-sharp';
  private static readonly CACHE_KEY = '_storefront_theme';

  private static readonly VARS = [
    '--brand', '--on-brand', '--brand-accent', '--brand-tint', '--brand-strong',
    '--bg', '--field-bg', '--text', '--text-muted', '--divider',
    '--picker-icon-filter',
    '--radius-sm', '--radius-md', '--radius-lg', '--radius-pill',
  ];

  apply(store: Store) {
    const root = document.documentElement;
    const flags: any = store && store.feature_flag ? store.feature_flag : {};
    const brand = store && store.storefront_brand_color;

    if (!flags['storefront_theming_enabled'] || !brand) {
      root.classList.remove(StorefrontThemeService.THEME_CLASS);
      root.classList.remove(StorefrontThemeService.SHARP_CLASS);
      StorefrontThemeService.VARS.forEach(v => root.style.removeProperty(v));
      this.clearCache();
      return;
    }

    const dark = !!store.storefront_dark_mode;
    const vars: Record<string, string> = {};
    vars['--brand'] = brand;
    vars['--on-brand'] = this.onBrand(brand);
    vars['--brand-accent'] = dark ? this.lighten(brand, 0.55) : this.ensureContrastOnWhite(brand);
    vars['--brand-tint'] = dark ? this.darken(brand, 0.75) : this.lighten(brand, 0.88);
    vars['--brand-strong'] = dark ? this.lighten(brand, 0.55) : this.ensureContrastOnWhite(this.darken(brand, 0.1));
    vars['--bg'] = dark ? '#171717' : '#FFFFFF';
    vars['--field-bg'] = dark ? '#262626' : '#F5F5F5';
    vars['--text'] = dark ? '#F5F5F5' : '#171717';
    vars['--text-muted'] = dark ? '#B3B3B3' : '#616161';
    vars['--divider'] = dark ? '#333333' : '#E0E0E0';
    vars['--picker-icon-filter'] = dark ? 'invert(1)' : 'none';

    // Corner style as tokens, so every component rounds the same way. Defaults for
    // unthemed stores live in styles.scss (`html { --radius-* }`) and match 'rounded'.
    // 'rounded' deliberately has NO pill shapes: buttons, toggles, chips and badges all share
    // the medium radius so the storefront reads as one design (`--radius-pill` = control radius).
    const isSharp = store.storefront_corner_style === 'sharp';
    vars['--radius-sm'] = isSharp ? '2px' : '8px';
    vars['--radius-md'] = isSharp ? '4px' : '12px';
    vars['--radius-lg'] = isSharp ? '4px' : '16px';
    vars['--radius-pill'] = isSharp ? '4px' : '12px';

    // Apply to DOM
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
    root.classList.add(StorefrontThemeService.THEME_CLASS);
    root.classList.toggle(StorefrontThemeService.SHARP_CLASS, isSharp);

    // Cache for instant restore on next page load
    this.saveCache(vars, isSharp);
  }

  /**
   * Instantly restore the theme from localStorage cache.
   * Call this as early as possible (e.g. AppComponent constructor)
   * so the page never flashes with unthemed colours.
   */
  restoreFromCache(): void {
    try {
      const raw = localStorage.getItem(StorefrontThemeService.CACHE_KEY);
      if (!raw) { return; }
      const cache = JSON.parse(raw);
      if (!cache || !cache.vars) { return; }
      const root = document.documentElement;
      Object.entries(cache.vars).forEach(([k, v]) => root.style.setProperty(k, v as string));
      root.classList.add(StorefrontThemeService.THEME_CLASS);
      root.classList.toggle(StorefrontThemeService.SHARP_CLASS, !!cache.sharp);
    } catch (e) {
      // Corrupt cache — ignore, theme will be applied when store loads
    }
  }

  private saveCache(vars: Record<string, string>, sharp: boolean): void {
    try {
      localStorage.setItem(StorefrontThemeService.CACHE_KEY,
        JSON.stringify({ vars, sharp }));
    } catch (e) { /* localStorage full — non-critical */ }
  }

  private clearCache(): void {
    try { localStorage.removeItem(StorefrontThemeService.CACHE_KEY); } catch (e) {}
  }

  // WCAG relative luminance; 0.179 is the crossover where near-black text
  // beats white for the 4.5:1 contrast requirement on a brand surface.
  private onBrand(hex: string): string {
    return this.relativeLuminance(hex) > 0.179 ? '#171717' : '#FFFFFF';
  }

  private relativeLuminance(hex: string): number {
    const c = (hex || '').replace('#', '');
    if (c.length !== 6) { return 0; }
    const parts = [0, 2, 4]
      .map(i => parseInt(c.substr(i, 2), 16) / 255)
      .map(v => v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
    return 0.2126 * parts[0] + 0.7152 * parts[1] + 0.0722 * parts[2];
  }

  private lighten(hex: string, amount: number): string {
    const c = (hex || '').replace('#', '');
    if (c.length !== 6) { return hex; }
    const channel = (i: number) => {
      const v = parseInt(c.substr(i, 2), 16);
      const lightened = Math.round(v + (255 - v) * amount).toString(16);
      return lightened.length === 1 ? '0' + lightened : lightened;
    };
    return '#' + channel(0) + channel(2) + channel(4);
  }

  private darken(hex: string, amount: number): string {
    const c = (hex || '').replace('#', '');
    if (c.length !== 6) { return hex; }
    const channel = (i: number) => {
      const v = parseInt(c.substr(i, 2), 16);
      const darkened = Math.round(v * (1 - amount)).toString(16);
      return darkened.length === 1 ? '0' + darkened : darkened;
    };
    return '#' + channel(0) + channel(2) + channel(4);
  }

  /**
   * Progressively darken a colour until it has at least 4.5:1 contrast
   * against white (#FFF). Bright brand colours like #FFB300 get auto-
   * adjusted so text remains readable on light backgrounds.
   */
  private ensureContrastOnWhite(hex: string): string {
    let color = hex;
    for (let i = 0; i < 10; i++) {
      const lum = this.relativeLuminance(color);
      // contrast ratio against white (luminance 1.0)
      const contrast = 1.05 / (lum + 0.05);
      if (contrast >= 4.5) { break; }
      color = this.darken(color, 0.12);
    }
    return color;
  }
}
